import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "@/components/ui/textarea";
import { useGetBusinessProfile } from "./useGetBusinessProfile";
import { useUpdateBusinessProfile } from "./useUpdateBusinessProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffect } from "react";
import ThemedSkeleton from "../../ui/ThemedSkeleton";
import { NaicsCodeSelector } from "./NaicsCodeSelector";
const FormSchemaPart1 = z.object({
  company_name: z.string().min(1, "This field is required."),
  company_address: z.string().min(1, "This field is required."),
  locations_description: z.string().optional(),
  uei: z.string().optional(),
  cage_code: z.string().optional(),
});

const FormSchemaPart2 = z.object({
  website: z.string().optional(),
  profile_description: z.string(),
  naics_codes: z.array(z.string()).optional(),
});

function BusinessProfileFormPart1() {
  const { data: profile, isLoading: isGetProfileLoading } =
    useGetBusinessProfile();
  const { mutate: updateBusinessProfile, isLoading: isUpdateProfileLoading } =
    useUpdateBusinessProfile();
  const form = useForm({
    resolver: zodResolver(FormSchemaPart1),
  });

  useEffect(() => {
    form.setValue("company_name", profile?.company_name);
    form.setValue("company_address", profile?.company_address);
    form.setValue("locations_description", profile?.locations_description);
    form.setValue("website", profile?.website);
    form.setValue("uei", profile?.uei);
    form.setValue("cage_code", profile?.cage_code);
  }, [profile, form]);

  function onSubmit(data) {
    updateBusinessProfile(data);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={`space-y-6 p-6 bg-background`}
      >
        <FormField
          control={form.control}
          name="company_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Company Name <span className="text-red-500">*</span>
              </FormLabel>
              <FormDescription>
                The company name to use when responding to solicitations.
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input disabled={isUpdateProfileLoading} {...field} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="company_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Company Address <span className="text-red-500">*</span>
              </FormLabel>
              <FormDescription>
                The company address to use when responding to solicitations.
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input
                    placeholder="e.g., 1234 Sam Rd, Washington, DC, 00000"
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="locations_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Region</FormLabel>
              <FormDescription>
                Please list the locations where your business operates.
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input
                    placeholder="e.g., New York, New Jersey, Connecticut, etc."
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="uei"
          render={({ field }) => (
            <FormItem>
              <FormLabel>UEI</FormLabel>
              <FormDescription>
                Enter your business's Unique Entity Identifier (UEI).
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input
                    placeholder="e.g., 1234567890"
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cage_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CAGE Code</FormLabel>
              <FormDescription>
                Enter your business's CAGE (Commercial and Government Entity) code.
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input
                    placeholder="e.g., 1A2B3"
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="ml-auto">
          <Button
            type="submit"
            className="ml-3 w-32"
            disabled={isGetProfileLoading}
          >
            Update profile
          </Button>
        </div>
      </form>
    </Form>
  );
}

function BusinessProfileFormPart2() {
  const { data: profile, isLoading: isGetProfileLoading } =
    useGetBusinessProfile();
  const { mutate: updateBusinessProfile, isLoading: isUpdateProfileLoading } =
    useUpdateBusinessProfile();
  const form = useForm({
    resolver: zodResolver(FormSchemaPart2),
  });

  useEffect(() => {
    form.setValue("website", profile?.website);
    form.setValue("profile_description", profile?.profile_description);
    form.setValue("naics_codes", profile?.naics_codes);
  }, [profile, form]);

  function onSubmit(data) {
    updateBusinessProfile(data);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={`space-y-6 p-6 bg-background`}
      >
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormDescription>
                The website is optional. However, if provided, the business
                description and NAICS code will be
                auto-generated by our AI.
              </FormDescription>
              <div className="flex gap-2">
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Input
                    placeholder="https://yourcompany.com"
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <Button
                  type="submit"  
                >
                  Generate Description & NAICS Code
                </Button>
                </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="profile_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Business Description
              </FormLabel>
              <FormDescription>
                Describe your business's core competencies, past government
                projects, and unique qualifications. (If a website is provided,
                this will be automatically generated)
              </FormDescription>
              <FormControl>
                {isGetProfileLoading ? (
                  <ThemedSkeleton className="w-11/12" />
                ) : (
                  <Textarea
                    placeholder="e.g., We are a small business specializing in..."
                    className="h-80"
                    disabled={isUpdateProfileLoading}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="naics_codes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>NAICS Codes</FormLabel>
              <FormDescription>
                Enter NAICS codes separated by commas. Spaces will be
                automatically removed. (If a website is provided, this will be
                automatically generated)
              </FormDescription>
              <FormControl>
                    <NaicsCodeSelector
                      placeholder="Search for industry codes..."
                      {...field}
                    />
                  </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="ml-auto">
          <Button
            type="submit"
            className="ml-3 w-32"
            disabled={isGetProfileLoading}
          >
            Update profile
          </Button>
        </div>
      </form>
    </Form>
  );
}

export { BusinessProfileFormPart1, BusinessProfileFormPart2 };
